import { Inject } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { EmptyObject } from 'apollo-angular/types';
import { DocumentNode } from 'graphql';
import { BehaviorSubject, Subscription } from 'rxjs';

export abstract class NXDataSourcePrime<T> {

    /**
     * We need to store the current variables. The reason is the filtering of the table:
     * - query.refetch() compares the current variables with the new variables. If they are the same, the query is not refetched.
     * - we want to have , e.g., a multiselect, that shows "nothing selected", but in the background the variables are set to 
     *   a specific value, e.g.: empty filter means load all tasks in state NEW, COMPLETED, ASSIGNED
     * - if the user has select all three states and clicks "clear filter", the states will be set back to NEW, COMPLETED, ASSIGNED
     * - this results in the "same" query as before, but watch query never resolves and the loading indicator stays on true
     * - we cannot directly use queryRef.variables - the values are a bit different, e.g. undefined vs. null
     */
    private currentVariables: string | undefined = undefined;
    private NXSubject = new BehaviorSubject<T[]>([]);
    // reference to the apollo query of the current page
    public queryRef: QueryRef<T>;
    // subscription to the apollo query
    public querySubscription: Subscription;
    public loadingSubject = new BehaviorSubject<boolean>(false);

    public loading = this.loadingSubject.asObservable();
    public totalElements: number;
    public data: any;
    constructor(
        private apollo: Apollo,
        @Inject('query') protected query: DocumentNode,
        @Inject('env') private environment: any
    ) {
    }

    disconnect(): void {
        this.NXSubject.complete();
        this.querySubscription?.unsubscribe();
        this.loadingSubject.complete();
    }

    subscribeToQuery(variables?: EmptyObject | undefined): void {
        // set the current variables to allow comparison
        if (variables) {
            this.currentVariables = JSON.stringify(variables);
        }
        this.loadingSubject.next(true);
        this.queryRef = this.apollo.watchQuery<T>({
            query: this.query,
            fetchPolicy: 'network-only',
            context: { __skipErrorAccordingCache__: true },
            errorPolicy: 'all',
            pollInterval: this.environment.pollingIntervall,
            variables: variables ? variables : undefined,
        });
        this.querySubscription = this.queryRef.valueChanges.subscribe(
            (result) => {
                this.loadingSubject.next(false);
                if (
                    result.data &&
                    result.networkStatus !== 2 &&
                    result.networkStatus !== 4
                ) {
                    this.handleSubscriptionResult(result.data);
                }
                if (result.errors) {
                    for (let error of result.errors) {
                        console.log(error);
                        // AppComponent.APM.captureError(error);
                    }
                }
            },
            (error) => {
                this.loadingSubject.next(false);
                // AppComponent.APM.captureError(error);
            }
        );
    }

    abstract handleSubscriptionResult(data: any): any;

    setSubject(data: any, count: number): void {
        this.NXSubject.next(data);
        this.totalElements = count;
        this.data = data;
    }

    refetchQuery(variables: any, force = false): void {
        // prevent refetching the query if the variables are the same - ignore if force is true
        if (!force && variables !== undefined && JSON.stringify(variables) === this.currentVariables) {
            this.loadingSubject.next(false);
            return;
        }
        this.currentVariables = JSON.stringify(variables);
        this.queryRef?.refetch(variables);
    }

    public static translateSortDirectionToSortOrder(sortDirection: string): number {
        return sortDirection === 'asc' ? 1 : -1;
    }

    public static translateSortOrderToSortDirection(sortOrder: number): string {
        return sortOrder === 1 ? 'asc' : 'desc';
    }
}
